import React, { useMemo } from 'react';
import LoadingSequence from 'assets/Loading/LoadingSequence';
import WidgetDiagnostics from './WidgetDiagnostics';
import { useWidgetFetch } from 'hooks/useWidgetFetch';
import { apiPath } from 'apiurl';

const STATE_META = [
  { key: 'C', label: 'Completed', color: '#198754', badgeClass: 'bg-success' },
  { key: 'R', label: 'Ready',     color: '#0d6efd', badgeClass: 'bg-primary' },
  { key: 'F', label: 'Failed',    color: '#dc3545', badgeClass: 'bg-danger'  },
];

const getBucket = (timeRange) => {
  if (timeRange === 'lastTwentyFourHours') return 'hour';
  if (timeRange === 'currentMonth') return 'day';
  if (timeRange === '6m' || timeRange === '1y') return 'month';
  return 'year';
};

const formatLabel = (item, bucket) => {
  const { Year, Month, Day, Hour } = item;
  if (bucket === 'hour')  return `${Year}-${String(Month).padStart(2,'0')}-${String(Day||1).padStart(2,'0')} ${String(Hour??0).padStart(2,'0')}:00`;
  if (bucket === 'day')   return `${Year}-${String(Month).padStart(2,'0')}-${String(Day||1).padStart(2,'0')}`;
  if (bucket === 'month') return `${Year}-${String(Month).padStart(2,'0')}`;
  return String(Year ?? 'Unknown');
};

function PeakActivityCard({ timeRange }) {
  const url = `${apiPath}/api/dashboard-status/cadence-timeline?timeRange=${timeRange}`;
  const { data, loading, timing } = useWidgetFetch(url, {
    ttl: 5 * 60 * 1000,
    swr: 10 * 60 * 1000,
    widgetId: 'Peak Activity',
  });

  const peak = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return null;
    const bucket = getBucket(timeRange);

    const byDate = {};
    data.forEach(item => {
      const key = `${item.Year}-${String(item.Month??'').padStart(2,'0')}-${String(item.Day??'').padStart(2,'0')}-${String(item.Hour??'').padStart(2,'0')}`;
      if (!byDate[key]) byDate[key] = { label: formatLabel(item, item.bucket || bucket), total: 0, states: {} };
      byDate[key].total += item.count;
      if (item.state) byDate[key].states[item.state] = (byDate[key].states[item.state] || 0) + item.count;
    });

    const top = Object.values(byDate).sort((a, b) => b.total - a.total)[0];
    if (!top) return null;
    return { label: top.label, runs: top.total, states: top.states };
  }, [data, timeRange]);

  const hasPeak = peak && peak.runs > 0;

  return (
    <div className="kpi-card kpi-card--status kpi-card--diagnostics-outside" style={{ position: 'relative' }}>
      <WidgetDiagnostics timing={timing} url={url} dataCount={Array.isArray(data) ? data.length : null} panelPlacement="above" />
      {loading ? <LoadingSequence message="" size="small" /> : (
        <>
          <p className="kpi-label">Peak Cadence Window</p>
          {hasPeak ? (
            <>
              <div className="kpi-value kpi-value--sm" style={{ marginBottom: '6px' }}>
                {peak.label}
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px' }}>
                {peak.runs.toLocaleString()} total runs
              </div>
              <div className="run-status-breakdown">
                {STATE_META.map(({ key, label, badgeClass }) => {
                  const count = peak.states[key] || 0;
                  if (count === 0) return null;
                  return (
                    <div className="run-status-row" key={key}>
                      <span className="run-label">{label}</span>
                      <span className={`badge ${badgeClass}`} style={{ fontSize: '13px' }}>
                        {count.toLocaleString()}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="kpi-value">--</div>
          )}
        </>
      )}
    </div>
  );
}

export default PeakActivityCard;
